import { VendorPayment } from "@features/vendorPayment/VendorPayment";
import { IPayoutInfluencer } from "@shared/lib/types/briefCampains";
import { Drawer, Table } from "@shared/ui";
import { usePayoutsColumns } from "@widgets/Payouts/hooks/usePayoutsColumns";
import { usePayoutsPage } from "@widgets/Payouts/hooks/usePayoutsPage";
import { PayoutsTab } from "@widgets/Payouts/lib/payoutTabs";
import { AddBillingDetails } from "@widgets/Payouts/ui/AddBillingDetails";
import { PayoutExportsPanel } from "@widgets/Payouts/ui/PayoutExportsPanel";
import { PayoutParsePreviewModal } from "@widgets/Payouts/ui/PayoutParsePreviewModal";
import { PayoutPayConfirmModal } from "@widgets/Payouts/ui/PayoutPayConfirmModal";
import { PayoutsPageHeader } from "@widgets/Payouts/ui/PayoutsPageHeader";
import { PayoutsStatusTabs } from "@widgets/Payouts/ui/PayoutsStatusTabs";
import { Modal } from "antd";
import React, { useState } from "react";
import styles from "./styles.module.scss";

export function Payouts() {
  const page = usePayoutsPage();
  const [paymentModalProps, setPaymentModalProps] = useState({
    visible: false,
    influencerId: "",
  });
  const [billingModalRecord, setBillingModalRecord] =
    useState<IPayoutInfluencer | null>(null);

  const columns = usePayoutsColumns({
    isCancelling: page.isCancelling,
    onCancelPayout: page.cancelVendorPayment,
    onViewPayments: (campaignInfluencerId) =>
      setPaymentModalProps({
        visible: true,
        influencerId: campaignInfluencerId,
      }),
    onAddBillingDetails: (record) => setBillingModalRecord(record),
    briefOptions: page.briefOptions,
    selectedBriefIds: page.selectedBriefIds,
    onBriefIdsChange: page.setSelectedBriefIds,
    briefSearch: page.briefSearch,
    onBriefSearchChange: page.setBriefSearch,
    areBriefsLoading: page.areBriefsLoading,
  });
  const showSelection = page.activeTab === PayoutsTab.Queued;
  const matchingCount = page.payouts?.pagination?.totalCount || 0;

  const closePaymentDrawer = () =>
    setPaymentModalProps((prev) => ({ ...prev, visible: false }));

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Payouts</h1>
      <p className={styles.subtitle}>
        All payouts in one list. Use status filters below search to narrow the
        table.
      </p>

      <Table
        className={styles.table}
        dataSource={page.payouts?.data}
        columns={columns}
        title={() => (
          <div>
            <PayoutsPageHeader
              matchingCount={matchingCount}
              searchQuery={page.searchQuery}
              onSearchChange={page.setSearchQuery}
              showPay={showSelection}
              selectedCount={page.selectedRowKeys.length}
              onPay={() => page.setIsPayOpen(true)}
              isDownloading={page.isDownloading}
              onDownload={page.downloadPayouts}
            />
            <PayoutsStatusTabs
              activeTab={page.activeTab}
              onChange={page.setActiveTab}
              facets={page.facets}
            />
            {page.activeTab === PayoutsTab.Processing ? (
              <PayoutExportsPanel
                exports={page.exports}
                isUploading={page.isUploading}
                isCancelling={page.isCancellingExport}
                onUpload={page.handleUpload}
                onCancel={page.cancelPayoutExport}
              />
            ) : null}
          </div>
        )}
        loading={page.isLoading}
        rowKey={(record: IPayoutInfluencer) => page.getRowKey(record)}
        scroll={{ x: 1800 }}
        rowSelection={
          showSelection
            ? {
                selectedRowKeys: page.selectedRowKeys,
                columnWidth: 40,
                onChange: (keys: React.Key[]) => page.setSelectedRowKeys(keys),
              }
            : undefined
        }
        pagination={{
          current: page.pageNo,
          pageSize: page.pageSize,
          total: matchingCount,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (nextPage: number, size: number) => {
            page.setPageNo(nextPage);
            page.setPageSize(size);
          },
        }}
      />

      <PayoutPayConfirmModal
        open={page.isPayOpen}
        items={page.selectedPayouts}
        isSubmitting={page.isCreating}
        onClose={() => page.setIsPayOpen(false)}
        onConfirm={page.confirmPay}
      />
      <PayoutParsePreviewModal
        open={Boolean(page.parsePreview)}
        fileName={page.parsePreview?.fileName}
        rows={page.parsePreview?.rows ?? []}
        isSubmitting={page.isSyncing}
        onClose={() => page.setParsePreview(null)}
        onConfirm={page.confirmParse}
      />

      <Modal
        open={Boolean(billingModalRecord)}
        onCancel={() => setBillingModalRecord(null)}
        footer={null}
        closable={false}
        destroyOnClose
        centered
        width={480}
        styles={{ content: { padding: 0 }, body: { padding: 0 } }}
      >
        {billingModalRecord ? (
          <AddBillingDetails
            vendorPaymentId={
              billingModalRecord.vendorPaymentId || billingModalRecord._id
            }
            creatorName={
              billingModalRecord.campaignInfluencer?.influencerName ||
              billingModalRecord.campaignInfluencer?.socialMediaName ||
              billingModalRecord.vendorBilling?.name
            }
            campaignName={billingModalRecord.campaignInfluencer?.brief?.name}
            initialValues={{
              // accountHolderName:
              //   billingModalRecord.vendorBilling?.name ||
              //   billingModalRecord.campaignInfluencer?.influencerName ||
              //   "",
              accountNumber:
                billingModalRecord.bankVerification?.bankAccountNumber || "",
              ifsc: billingModalRecord.bankVerification?.ifscCode || "",
            }}
            onClose={() => setBillingModalRecord(null)}
            onCancel={() => setBillingModalRecord(null)}
            onSaveAndQueue={() => {
              setBillingModalRecord(null);
            }}
          />
        ) : null}
      </Modal>

      <Drawer
        title="Payment"
        destroyOnClose
        footer={null}
        width={1200}
        open={paymentModalProps.visible}
        onClose={closePaymentDrawer}
      >
        {paymentModalProps.visible ? (
          <VendorPayment
            campaignInfluencerId={paymentModalProps.influencerId || ""}
            onClose={closePaymentDrawer}
          />
        ) : null}
      </Drawer>
    </div>
  );
}
